import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { AiHeader } from "./AiHeader";
import { type GeneratedImage } from "./AiImageGallery";
import { PromptForm } from "./PromptForm";
import { RefinementFormMobile } from "./RefinementFormMobile";
import { SingleImageRefinementView } from "./SingleImageRefinementView";

export type AiRefinementViewProps = {
  refinementImage: GeneratedImage;
  refinementPrompt: string;
  isRefining: boolean;
  isSelected: boolean;
  error: string | undefined;
  onPromptChange: (prompt: string) => void;
  onRefine: (prompt: string) => void;
  onCancel: () => void;
  onSelect: (imageId: number) => void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
};

export function AiRefinementView({
  refinementImage,
  refinementPrompt,
  isRefining,
  isSelected,
  error,
  onPromptChange,
  onRefine,
  onCancel,
  onSelect,
  canUndo,
  canRedo,
  onUndo,
  onRedo
}: AiRefinementViewProps) {
  const { isDesktop } = useResponsive();

  if (isDesktop) {
    return (
      <div className="flex h-full gap-6">
        {/* Left Side - Refinement Form */}
        <div className="flex w-1/2 flex-col">
          <AiHeader isRefining={true} />

          <PromptForm
            prompt={refinementPrompt}
            onPromptChange={onPromptChange}
            onGenerate={onRefine}
            isLoading={isRefining}
            error={error}
            isRefineMode={true}
            canUndo={canUndo}
            canRedo={canRedo}
            onUndo={onUndo}
            onRedo={onRedo}
          />
        </div>

        {/* Right Side - Single Image Preview */}
        <div className="w-1/2">
          <SingleImageRefinementView
            image={refinementImage}
            onBack={onCancel}
            onSelect={() => onSelect(refinementImage.id)}
            isSelected={isSelected}
            isRefining={isRefining}
          />
        </div>
      </div>
    );
  }

  // Mobile view
  return (
    <div className="flex h-full flex-col gap-3">
      {/* Single Image Preview */}
      <div className="flex-1">
        <SingleImageRefinementView
          image={refinementImage}
          onBack={onCancel}
          onSelect={() => onSelect(refinementImage.id)}
          isSelected={isSelected}
          isRefining={isRefining}
          headerText="Edit Your Design"
        />
      </div>

      {/* Refinement Form */}
      <RefinementFormMobile
        prompt={refinementPrompt}
        onPromptChange={onPromptChange}
        onRefine={onRefine}
        isLoading={isRefining}
        error={error}
        canUndo={canUndo}
        canRedo={canRedo}
        onUndo={onUndo}
        onRedo={onRedo}
      />
    </div>
  );
}
